<template>
  <BottomSheet :is-open="true" :title="productName" height="90dvh" tablet-height="66vh" @close="stcUIStore.closeBottomSheet">
    <div class="stc-product-details-content">
      <ImageGallery v-if="carouselImages?.length" :disable-overlay="true" class="stc-product-slideshow" />
      <Traits
        v-model:quantity="quantity"
        :min-quantity="minQuantity"
        :product-id="productId"
        :product="product"
        class="stc-product-traits"
        show-quantity-selector
      />
      <LowAvailability :product-id="productId" class="stc-product-low-stock" />

      <Toast v-if="toastMessage" :duration="2000" placement="center" class="stc-compact-product__toast" @destroy="toastMessage = ''">
        {{ toastMessage }}
      </Toast>
    </div>

    <template #footer>
      <div class="stc-product-details-footer">
        <div class="stc-product-details-actions">
          <a
            v-if="productUrl"
            :href="productUrl"
            class="button secondary expanded"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="stc-member-details-view-details"
            @click="tagSTCMemberPDPClick"
          >
            View details
          </a>
          <Button
            v-if="!isSelected"
            variant="primary"
            expanded
            :disabled="multiATBStore.isLoading"
            data-testid="stc-member-details-select"
            @click="handleSelectOrUpdate"
          >
            Select
          </Button>
          <Button
            v-else
            variant="primary"
            expanded
            :disabled="multiATBStore.isLoading"
            data-testid="stc-member-details-update"
            @click="handleSelectOrUpdate"
          >
            Update
          </Button>
        </div>
      </div>
    </template>
  </BottomSheet>
</template>

<script lang="ts" setup>
import { computed, provide, ref } from 'vue';

import Button from '@atomic-ui/button/Button.vue';
import Toast from '@atomic-ui/toast/ToastRenderer';
import LowAvailability from '~/modules/availability/components/LowAvailability';
import Traits from './Traits.vue';
import ImageGallery from '~/components/shopTheCollection/ImageGallery.vue';
import BottomSheet from '~/components/common/BottomSheet.vue';

import { useBrand } from '~/composables/useBrand';
import useProductStore from '~/stores/useProductStore';
import { useCollectionSelectionStore } from '~/stores/useCollectionSelectionStore';
import { useProductProvide } from '~/modules/product/composables/useProductProvide';
import { useUpcValidatorProvide, useUpcValidator } from '~/modules/upc/composables/useUpcValidator';
import useUpc from '~/modules/upc/composables/useUpc';
import { useMarketPlace } from '~/composables/useMarketPlace';
import { useProductPriceProvide } from '~/modules/price/composables/useProductPriceProvide';
import { useCreateTypeStore } from '@nuxt-common/media-card-large/app/modules/type';
import TypeParser from '@nuxt-common/media-card-large/app/modules/type/parsers/TypeParser';
import { useImageryStateProvide } from '~/composables/useImageryState';

import { useProductPrice } from '~/modules/price/composables/useProductPrice';
import { getLowestPrice } from '~/modules/price/parsers';
import { useCommonProductGetters } from '~/composables/useCommonProductGetters';
import { useImagesProvide } from '~/composables/images/useImages';
import { useShopTheCollectionUIStore } from '~/stores/useShopTheCollectionUIStore';
import { useMultiATBStore } from '~/stores/useMultiATBStore';
import { useUpcPreselectBasic } from '~/modules/upc/composables/useUpcPreselectBasic';
import { tagSTCMemberPDPClick, tagSTCSelectItem } from '~/features/analytics/shopTheCollection';

const props = defineProps<{
  productId: ProductId;
}>();

const { isMcom } = useBrand();

const stcUIStore = useShopTheCollectionUIStore();
const multiATBStore = useMultiATBStore();

// Provide STC context for underlying components
provide('isSTC', true);

const selectionStore = useCollectionSelectionStore();
const memberProducts = useProductStore().getAllMembers;
const product = memberProducts[String(props.productId)];

// Initialize local context for member product
useProductProvide(props.productId, product);
useUpcValidatorProvide();
const { sellerId } = useMarketPlace();
useProductPriceProvide({ id: props.productId, product });
useImageryStateProvide();

useCreateTypeStore({
  id: props.productId,
  data: TypeParser(product, { defaultToFirst: isMcom }),
});

// Apply pre-selected UPC from UI store if available
if (stcUIStore.activeBottomSheetUpcId) {
  useUpcPreselectBasic(stcUIStore.activeBottomSheetUpcId);
}

const selectedPrice = useProductPrice();
const { productUrl, productName } = useCommonProductGetters();
const { primaryImageData, carouselImages } = useImagesProvide();

const { selectedUpcIdOrNull: selectedUpcId, selectedUpcNumber } = useUpc();
const { upcValidator } = useUpcValidator();

const toastMessage = ref('');
const quantity = ref(1);

const isSelected = computed(() => {
  if (!selectedUpcId.value) return false;
  return selectionStore.getQuantityForUpc(props.productId, selectedUpcId.value) > 0;
});

const minQuantity = computed(() => (isSelected.value ? 0 : 1));
const isSelectionDisabled = computed(() => !!upcValidator());
const primaryImage = computed(() => primaryImageData.value?.path);

// Initialize quantity if already selected
if (isSelected.value && selectedUpcId.value) {
  quantity.value = selectionStore.getQuantityForUpc(props.productId, selectedUpcId.value);
}

const handleSelectOrUpdate = () => {
  if (multiATBStore.isLoading) return;

  if (isSelectionDisabled.value) {
    toastMessage.value = upcValidator();
    return;
  }

  if (!selectedUpcId.value) return;

  const priceFallback = product?.pricing?.price?.regularPrice?.values?.[0]?.value || 0;
  const price = getLowestPrice(selectedPrice.value) || priceFallback;

  const offer = selectedPrice.value?.badgesList?.[0];
  const promoMessaging = offer?.promoId || 'no promo messaging';

  selectionStore.updateSelection(props.productId, selectedUpcId.value, {
    quantity: quantity.value,
    price,
    priceTypeId: selectedPrice.value?.priceTypeId || 'ZERO',
    promoMessaging,
    upcNumber: selectedUpcNumber.value,
    imageUrl: primaryImage.value || '',
    alt: `${productName} image`,
    sellerId: sellerId.value,
  });

  tagSTCSelectItem();
  stcUIStore.closeBottomSheet();
};
</script>

<style lang="scss" scoped>
@import '~settingSCSS';

.stc-product-details-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  position: relative;
}

.stc-compact-product__toast {
  max-width: 90%;
  z-index: 2001;
}

.stc-product-slideshow {
  width: 100%;
  max-height: 65%;
  margin: 0 auto;

  @include breakpoint(medium up) {
    width: 100%;
    max-height: none;
    max-width: 100%;
    padding: 0px 16px;
  }

  @include stc-media-stretch;
}

.stc-product-traits {
  width: 100%;
  @include padding-top-s;
  @include padding-bottom-s;
  @include padding-left-xs;
  @include padding-right-xs;

  :deep(.credit-finance-container),
  :deep(.additional-fees) {
    display: none !important;
  }

  @include breakpoint(small only) {
    :deep(.expandable) {
      @include margin-right-xxs;
      margin-left: rem-calc(-24);
    }

    :deep(.overflow-gutters-item) {
      @include padding-right-z;
    }
  }
}

.stc-product-low-stock {
  width: 100%;
  padding: 0 16px;
  @include margin-bottom-xs;
}

.stc-product-details-footer {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.stc-product-details-actions {
  display: flex;
  gap: 12px;
}
</style>


